import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "./History.css";

import veranda1 from "../../../assets/img/Stories/Veranda/1.jpg";
import veranda2 from "../../../assets/img/Stories/Veranda/2.jpg";
import veranda3 from "../../../assets/img/Stories/Veranda/3.jpg";
import veranda4 from "../../../assets/img/Stories/Veranda/4.jpg";
import veranda5 from "../../../assets/img/Stories/Veranda/5.jpg";

import parking1 from "../../../assets/img/Stories/Parking/1.jpg";
import parking2 from "../../../assets/img/Stories/Parking/2.jpg";
import parking3 from "../../../assets/img/Stories/Parking/3.jpg";
import parking4 from "../../../assets/img/Stories/Parking/4.jpg";
import parking5 from "../../../assets/img/Stories/Parking/5.jpg";
import parking6 from "../../../assets/img/Stories/Parking/6.jpg";
import parking7 from "../../../assets/img/Stories/Parking/7.jpg";

import kalyan1 from "../../../assets/img/Stories/Kalyan-Lunch/1.jpg";
import kalyan2 from "../../../assets/img/Stories/Kalyan-Lunch/2_new.jpg";

import vinil1 from "../../../assets/img/Stories/Vynil-Room/1.jpg";
import vinil2 from "../../../assets/img/Stories/Vynil-Room/2.jpg";
import vinil3 from "../../../assets/img/Stories/Vynil-Room/3.jpg";
import vinil4 from "../../../assets/img/Stories/Vynil-Room/4.jpg";
import vinil5 from "../../../assets/img/Stories/Vynil-Room/5.jpg";
import vinil6 from "../../../assets/img/Stories/Vynil-Room/6.jpg";

import radiola1 from "../../../assets/img/Stories/Radiola-Room/1.jpg";
import radiola2 from "../../../assets/img/Stories/Radiola-Room/2.jpg";
import radiola3 from "../../../assets/img/Stories/Radiola-Room/3.jpg";
import radiola4 from "../../../assets/img/Stories/Radiola-Room/4.jpg";
import radiola5 from "../../../assets/img/Stories/Radiola-Room/5.jpg";
import radiola6 from "../../../assets/img/Stories/Radiola-Room/6.jpg";

import studio1 from "../../../assets/img/Stories/Studio-54/1.jpg";
import studio2 from "../../../assets/img/Stories/Studio-54/2.jpg";
import studio3 from "../../../assets/img/Stories/Studio-54/3.jpg";
import studio4 from "../../../assets/img/Stories/Studio-54/4.jpg";
import studio5 from "../../../assets/img/Stories/Studio-54/5.jpg";

import { useSelector } from "react-redux";
import { translations, normalizeLang } from "../../../i18n";

const INTERVAL = 3500;
const TAP_THRESHOLD = 200;

const preload = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
    if (img.decode) {
      img
        .decode()
        .then(() => resolve(true))
        .catch(() => { });
    }
  });

function useViewedStories() {
  const load = () => {
    try {
      const saved = localStorage.getItem("viewedStories");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  };
  const [viewed, setViewed] = useState(load);
  const markViewed = (idx) => {
    setViewed((prev) => {
      const next = new Set(prev).add(idx);
      try {
        localStorage.setItem("viewedStories", JSON.stringify([...next]));
      } catch { }
      return next;
    });
  };
  return { viewed, markViewed };
}

function useRafTimer({ playing, duration, onDone, progressEl, keyToken }) {
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const carryRef = useRef(0);
  const stopRaf = () => cancelAnimationFrame(rafRef.current);

  const lastKeyRef = useRef(null);

  const tick = useCallback(() => {
    const now = performance.now();
    const elapsed = carryRef.current + (now - startRef.current);
    const ratio = Math.max(0, Math.min(1, elapsed / duration));

    if (progressEl?.current) {
      progressEl.current.style.transition = "none";
      progressEl.current.style.width = `${ratio * 100}%`;
    }

    if (elapsed >= duration) {
      carryRef.current = 0;
      onDone?.();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [duration, onDone, progressEl]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        if (playing) carryRef.current += performance.now() - startRef.current;
        stopRaf();
      } else if (playing) {
        startRef.current = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [playing, tick]);

  useEffect(() => {
    if (!playing) {
      if (startRef.current)
        carryRef.current += performance.now() - startRef.current;
      stopRaf();
      return;
    }

    if (lastKeyRef.current !== keyToken) {
      carryRef.current = 0;
      lastKeyRef.current = keyToken;
    }

    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => stopRaf();
  }, [playing, duration, keyToken, tick]);

  const reset = useCallback(() => {
    carryRef.current = 0;
    stopRaf();
    if (progressEl?.current) {
      progressEl.current.style.transition = "none";
      progressEl.current.style.width = "0%";
    }
  }, [progressEl]);

  return { reset };
}

export default function History({ stories = [] }) {
  const user = useSelector((state) => state.user.data);
  const lang = normalizeLang(user?.languageCode);
  const t = translations[lang];

  const initialStories = useMemo(() => [
    {
      date: t.parkingTitle || "Парковка",
      images: [parking1, parking2, parking3, parking4, parking5, parking6, parking7],
    },
    { date: t.hookahLunch, images: [kalyan1, kalyan2] },
    { date: t.vinylRoom, images: [vinil1, vinil2, vinil3, vinil4, vinil5, vinil6] },
    { date: t.radiolaRoom, images: [radiola1, radiola2, radiola3, radiola4, radiola5, radiola6] },
    { date: t.veranda, images: [veranda1, veranda2, veranda3, veranda4, veranda5] },
    { date: t.studio54, images: [studio1, studio2, studio3, studio4, studio5] },
  ], [t]);

  const allStories = useMemo(
    () => [...initialStories, ...stories],
    [initialStories, stories]
  );

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dayIdx, setDayIdx] = useState(0);
  const [storyIdx, setStoryIdx] = useState(0);

  const [paused, setPaused] = useState(false);
  const pressStartedAt = useRef(0);

  const [imageKey, setImageKey] = useState(0);
  const [fadeOutImage, setFadeOutImage] = useState(false);

  const { viewed, markViewed } = useViewedStories();

  const barsRef = useRef([]);
  const activeBarRef = useRef(null);
  const imgRef = useRef(null);

  const loadedRef = useRef(new Set());
  const navLockRef = useRef(false);
  const prevDisplayedSrcRef = useRef(null);

  const preloadAndMark = useCallback(async (src) => {
    if (!src) return true;
    if (loadedRef.current.has(src)) return true;
    const ok = await preload(src);
    if (ok) loadedRef.current.add(src);
    return ok;
  }, []);

  const [displayedSrc, setDisplayedSrc] = useState(null);
  const targetSrc = allStories[dayIdx]?.images?.[storyIdx] ?? null;

  const [ready, setReady] = useState(false);
  const [barReady, setBarReady] = useState(false);

  const token = () => `${dayIdx}-${storyIdx}`;

  const { reset: resetTimer } = useRafTimer({
    playing: open && ready && barReady && !paused,
    duration: INTERVAL,
    onDone: () => nextStory(),
    progressEl: activeBarRef,
    keyToken: token(),
  });

  useLayoutEffect(() => {
    setReady(false);
    setBarReady(false);
    resetTimer();
  }, [dayIdx, storyIdx, resetTimer]);

  useLayoutEffect(() => {
    if (!open) return;
    barsRef.current = barsRef.current.slice(
      0,
      allStories[dayIdx].images.length
    );
    activeBarRef.current = barsRef.current[storyIdx] ?? null;
    const bars = barsRef.current;
    bars.forEach((bar, i) => {
      if (!bar) return;
      bar.style.transition = "none";
      bar.style.width = i < storyIdx ? "100%" : "0%";
    });
    setBarReady(!!activeBarRef.current);
  }, [open, dayIdx, storyIdx, allStories, ready]);

  useEffect(() => {
    if (!open || closing) return;

    resetTimer();
    const prevSrc = prevDisplayedSrcRef.current;
    const currentTargetSrc = targetSrc;
    const currentDayIdx = dayIdx;
    const currentStoryIdx = storyIdx;
    let cancelled = false;
    let fadeTimeout = null;

    const loadNewImage = async () => {
      if (cancelled) return;
      setReady(false);

      await preloadAndMark(currentTargetSrc);
      if (cancelled) return;

      setDisplayedSrc(currentTargetSrc);
      prevDisplayedSrcRef.current = currentTargetSrc;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled && !closing) {
            setReady(true);
          }
        });
      });

      const nextSrc = allStories[currentDayIdx]?.images?.[currentStoryIdx + 1];
      if (nextSrc) preloadAndMark(nextSrc);
    };

    if (prevSrc && prevSrc !== currentTargetSrc) {
      setFadeOutImage(true);

      fadeTimeout = setTimeout(() => {
        if (cancelled) return;
        setImageKey((prev) => prev + 1);
        setFadeOutImage(false);
        loadNewImage();
      }, 280);
    } else {
      setFadeOutImage(false);
      if (!prevSrc) {
        setDisplayedSrc(currentTargetSrc);
        prevDisplayedSrcRef.current = currentTargetSrc;
      }
      loadNewImage();
    }

    return () => {
      cancelled = true;
      if (fadeTimeout) clearTimeout(fadeTimeout);
    };
  }, [
    open,
    closing,
    dayIdx,
    storyIdx,
    targetSrc,
    allStories,
    resetTimer,
    preloadAndMark,
  ]);

  const setBarRef = (el, i) => {
    barsRef.current[i] = el;
    if (i === storyIdx) {
      activeBarRef.current = el;
      setBarReady(!!el);
    }
  };

  const closeViewer = () => {
    setFadeOutImage(true);
    setClosing(true);
    resetTimer();
    setPaused(true);

    setTimeout(() => {
      setReady(false);
      setBarReady(false);
      setDisplayedSrc(null);
      prevDisplayedSrcRef.current = null;
      setFadeOutImage(false);
      setOpen(false);
      setClosing(false);
      setPaused(false);
    }, 280);
  };

  const openViewer = (i) => {
    setDayIdx(i);
    setStoryIdx(0);
    setOpen(true);
    setClosing(false);
    prevDisplayedSrcRef.current = null;
    setFadeOutImage(false);
  };

  const nextDay = () => {
    markViewed(dayIdx);
    if (dayIdx < allStories.length - 1) {
      setDayIdx((d) => d + 1);
      setStoryIdx(0);
    } else {
      closeViewer();
    }
  };

  const prevDay = () => {
    if (dayIdx > 0) {
      const prevLen = allStories[dayIdx - 1].images.length;
      setDayIdx((d) => d - 1);
      setStoryIdx(prevLen - 1);
    }
  };

  const nextStory = async () => {
    if (navLockRef.current) return;

    const isLastInDay = storyIdx >= allStories[dayIdx].images.length - 1;

    if (!isLastInDay) {
      const nextSrc = allStories[dayIdx].images[storyIdx + 1];

      if (!loadedRef.current.has(nextSrc)) {
        navLockRef.current = true;
        setPaused(true);
        await preloadAndMark(nextSrc);
        setPaused(false);
        navLockRef.current = false;
      }

      if (loadedRef.current.has(nextSrc)) {
        setStoryIdx((i) => i + 1);
      }
      return;
    }

    const firstNext = allStories[dayIdx + 1]?.images?.[0];
    if (firstNext && !loadedRef.current.has(firstNext)) {
      navLockRef.current = true;
      setPaused(true);
      await preloadAndMark(firstNext);
      setPaused(false);
      navLockRef.current = false;
    }

    markViewed(dayIdx);
    nextDay();
  };

  const prevStory = () => {
    if (storyIdx > 0) setStoryIdx((i) => i - 1);
    else prevDay();
  };

  const handlePointerDown = () => {
    pressStartedAt.current = performance.now();
    setPaused(true);
  };
  const handlePointerUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const elapsed = performance.now() - pressStartedAt.current;
    setPaused(false);
    if (elapsed < TAP_THRESHOLD) {
      const { left, width } = e.currentTarget.getBoundingClientRect();
      const x = e.clientX ?? e.nativeEvent?.clientX ?? 0;
      x - left < width / 2 ? prevStory() : nextStory();
    }
  };

  const onViewerBgClick = (e) => {
    if (e.target === e.currentTarget || e.target.classList.contains("viewer")) {
      if (storyIdx === allStories[dayIdx].images.length - 1) markViewed(dayIdx);
      closeViewer();
    }
  };

  const handleViewerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <div className="thumbs">
        {allStories.map((g, i) => (
          <div key={g.date} style={{ textAlign: "center" }}>
            <div className={`thumbGradient ${viewed.has(i) ? "viewed" : ""}`}>
              <div className="thumbInner">
                <button
                  className="thumb"
                  style={{ backgroundImage: `url(${g.images[0]})` }}
                  onClick={() => openViewer(i)}
                />
              </div>
            </div>
            <p className="thumbDate">{g.date}</p>
          </div>
        ))}
      </div>

      {open &&
        createPortal(
          <div
            className={`viewer ${closing ? "closing" : ""}`}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onClick={onViewerBgClick}
            onMouseDown={handleViewerClick}
            onTouchStart={handleViewerClick}
            style={{
              opacity: closing ? 0 : 1,
              pointerEvents: closing ? "none" : "auto",
              backgroundColor: "#fef8ea",
              willChange: closing ? "opacity" : "auto",
            }}
          >
            <div className="storyHeader" onClick={handleViewerClick}>
              <div className="progressRow" key={`row-${dayIdx}`}>
                {allStories[dayIdx].images.map((_, i) => (
                  <span className="progressBg" key={i}>
                    <span
                      className="progressFg"
                      ref={(el) => setBarRef(el, i)}
                    />
                  </span>
                ))}
              </div>
              <div className="storyTitle">{allStories[dayIdx].date}</div>
            </div>

            <div className="storyImgWrapper" onClick={handleViewerClick}>
              {(displayedSrc || targetSrc) && !closing && (
                <img
                  ref={imgRef}
                  key={`${dayIdx}-${storyIdx}-${imageKey}`}
                  src={displayedSrc || targetSrc}
                  alt=""
                  className={`storyImg ${fadeOutImage || closing
                    ? "fadeOut"
                    : ready
                      ? "ready"
                      : "loading"
                    }`}
                  draggable={false}
                  onLoad={() => {
                    if (!fadeOutImage && !closing) {
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          if (!fadeOutImage && !closing) {
                            setReady(true);
                          }
                        });
                      });
                    }
                  }}
                  onError={() => {
                    if (!closing) {
                      setReady(true);
                    }
                  }}
                />
              )}
            </div>

            <button
              className="close"
              onClick={(e) => {
                e.stopPropagation();
                closeViewer();
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>,
          document.body
        )}
    </>
  );
}
